import { useEffect } from 'react';
import { useRouteMatch } from 'react-router';

import { visitPaths } from '~libs.desktop.shared.paths';

import { AiAssistantMainMenuTileItemFinal } from './mainMenuTileItem/AiAssistantMainMenuTileItemFinal';
import { AiAssistantMainMenuTileItemIdle } from './mainMenuTileItem/AiAssistantMainMenuTileItemIdle';
import { TranscriptionStartAction } from './mainMenuTileItem/AiAssistantMainMenuTileItemIdle.actions';
import { AiAssistantMainMenuTileItemMobilePending } from './mainMenuTileItem/AiAssistantMainMenuTileItemMobilePending';
import { AiAssistantMainMenuTileItemMobileRecording } from './mainMenuTileItem/AiAssistantMainMenuTileItemMobileRecording';
import { AiAssistantMainMenuTileItemPaused } from './mainMenuTileItem/AiAssistantMainMenuTileItemPaused';
import { AiAssistantMainMenuTileItemPending } from './mainMenuTileItem/AiAssistantMainMenuTileItemPending';
import {
   TranscriptDiscardAction,
   VisitTranscriptSummarizeAction,
} from './mainMenuTileItem/AiAssistantMainMenuTileItemPaused.actions';
import { AiAssistantMainMenuTileItemRecording } from './mainMenuTileItem/AiAssistantMainMenuTileItemRecording';
import { TranscriptionSessionEndAction } from './mainMenuTileItem/AiAssistantMainMenuTileItemRecording.actions';
import { AiAssistantMainMenuTileItemSummaryModalOpener } from './mainMenuTileItem/AiAssistantMainMenuTileItemSummaryModal.opener';
import type { AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback } from './mainMenuTileItem/AiAssistantMainMenuTileItemSummaryModal.types';
import { AiAssistantMobileRecordingEventListener } from './mainMenuTileItem/AiAssistantMobileRecordingEventListener';
import { aiAssistantRecordingStateVisitor } from './AiAssistantMainMenuTileItem.utils';
import { AiAssistantTranscriptionProvider } from './shared/AiAssistantTranscription.context';
import { AiAssistantVisitSummaryProvider } from './shared/AiAssistantVisitSummary.context';
import { useAiAssistantNotifyMicrophone } from './shared/useAiAssistantNotifyMicrophone';
import { useAiAssistantNotifyMicrophoneKeepalive } from './shared/useAiAssistantNotifyMicrophoneKeepalive';
import { useAiAssistantRecordingGlobalState } from './shared/useAiAssistantRecordingGlobalState';

type AiAssistantMainMenuTileItemProps = {
   onAcceptSummary: AiAssistantMainMenuTileItemSummaryModalAcceptSummaryCallback;
};

// eslint-disable-next-line max-lines-per-function
export const AiAssistantMainMenuTileItem = (props: AiAssistantMainMenuTileItemProps): JSX.Element | null => {
   const { state, reset } = useAiAssistantRecordingGlobalState();
   const { execute: notifyMicrophone } = useAiAssistantNotifyMicrophone();
   const notifyMicrophoneKeepalive = useAiAssistantNotifyMicrophoneKeepalive();
   const visitRouteMatch = useRouteMatch(visitPaths.VISIT_PATH);
   const onVisitRoute = visitRouteMatch !== null;

   useEffect(() => {
      if (!onVisitRoute) {
         reset();
         notifyMicrophone('visitExited');
      }
   }, [onVisitRoute, reset, notifyMicrophone]);

   useEffect(() => {
      if (!onVisitRoute) {
         return undefined;
      }

      const handlePageHide = (): void => notifyMicrophoneKeepalive('visitExited');

      window.addEventListener('pagehide', handlePageHide);

      return () => window.removeEventListener('pagehide', handlePageHide);
   }, [onVisitRoute, notifyMicrophoneKeepalive]);

   if (!onVisitRoute) {
      return null;
   }

   return (
      <AiAssistantMainMenuTileItemSummaryModalOpener onAcceptSummary={props.onAcceptSummary}>
         {({ openModal }) => (
            <AiAssistantTranscriptionProvider>
               <AiAssistantVisitSummaryProvider onSummarySuccess={openModal}>
                  <>
                     <AiAssistantMobileRecordingEventListener />
                     {aiAssistantRecordingStateVisitor(state, {
                        visitIdle: () => (
                           <TranscriptionStartAction>
                              {({ startTranscription }) => (
                                 <AiAssistantMainMenuTileItemIdle onStartTranscription={startTranscription} />
                              )}
                           </TranscriptionStartAction>
                        ),
                        visitRecording: (recordingState) => (
                           <TranscriptionSessionEndAction>
                              {({ endTranscriptionSession, discardTranscriptionSession }) => (
                                 <VisitTranscriptSummarizeAction>
                                    {({ summarizeTranscript }) => (
                                       <AiAssistantMainMenuTileItemRecording
                                          accumulatedSeconds={recordingState.accumulatedSeconds}
                                          sessionStartedAt={recordingState.sessionStartedAt}
                                          onEndTranscriptionSession={endTranscriptionSession}
                                          onDiscardTranscriptionSession={discardTranscriptionSession}
                                          onSummarizeTranscript={summarizeTranscript}
                                       />
                                    )}
                                 </VisitTranscriptSummarizeAction>
                              )}
                           </TranscriptionSessionEndAction>
                        ),
                        visitMobileRecording: () => <AiAssistantMainMenuTileItemMobileRecording />,
                        visitPaused: (pausedState) => (
                           <TranscriptionStartAction>
                              {({ startTranscription }) => (
                                 <VisitTranscriptSummarizeAction>
                                    {({ summarizeTranscript }) => (
                                       <TranscriptDiscardAction>
                                          {({ discardTranscript }) => (
                                             <AiAssistantMainMenuTileItemPaused
                                                accumulatedSeconds={pausedState.accumulatedSeconds}
                                                onStartTranscription={startTranscription}
                                                onSummarizeTranscript={summarizeTranscript}
                                                onDiscardTranscript={discardTranscript}
                                             />
                                          )}
                                       </TranscriptDiscardAction>
                                    )}
                                 </VisitTranscriptSummarizeAction>
                              )}
                           </TranscriptionStartAction>
                        ),
                        visitPending: () => (
                           <TranscriptionStartAction>
                              {({ startTranscription }) => (
                                 <AiAssistantMainMenuTileItemPending onStartTranscription={startTranscription} />
                              )}
                           </TranscriptionStartAction>
                        ),
                        visitMobilePending: () => <AiAssistantMainMenuTileItemMobilePending />,
                        visitFinal: () => <AiAssistantMainMenuTileItemFinal onShowNote={() => openModal()} />,
                     })}
                  </>
               </AiAssistantVisitSummaryProvider>
            </AiAssistantTranscriptionProvider>
         )}
      </AiAssistantMainMenuTileItemSummaryModalOpener>
   );
};
